// Service layer for shopping list operations
// - Provides CRUD functions and helpers for shopping list items scoped to a household
// - Encapsulates Supabase interactions and error handling

import {
  type ShoppingListItem as DBShoppingListItem,
  supabase,
} from "../lib/supabase";

export type ShoppingListItem = DBShoppingListItem;

// Public API for shopping list data
// - getItems: fetch items for current user's household, optional purchased filter
// - addItem: create a new shopping list item for the household
// - updateItem: apply partial updates and set updated_at timestamp
// - deleteItem: remove an item by id
// - markAsPurchased / markAsUnpurchased: convenience wrappers for toggling purchased flag
export const shoppingListService = {
  async getItems(
    householdId: string,
    purchased?: boolean,
  ): Promise<ShoppingListItem[]> {
    let query = supabase
      .from("shopping_list_items")
      .select(
        "id, user_id, household_id, item_name, quantity, unit, category, purchased, created_at, updated_at",
      )
      .eq("household_id", householdId)
      .order("created_at", { ascending: false });

    if (purchased !== undefined) {
      query = query.eq("purchased", purchased);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async addItem(
    item_name: string,
    quantity: number | null,
    unit: string | null,
    category: string | null,
    householdId: string,
    userId: string,
  ): Promise<ShoppingListItem> {
    const { data, error } = await supabase
      .from("shopping_list_items")
      .insert([
        {
          user_id: userId,
          household_id: householdId,
          item_name,
          quantity,
          unit,
          category,
          purchased: false,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateItem(
    id: string,
    updates: Partial<ShoppingListItem>,
  ): Promise<ShoppingListItem> {
    const { data, error } = await supabase
      .from("shopping_list_items")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteItem(id: string): Promise<void> {
    const { error } = await supabase
      .from("shopping_list_items")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },

  async deleteItems(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await supabase
      .from("shopping_list_items")
      .delete()
      .in("id", ids);

    if (error) throw error;
  },

  async markAsPurchased(id: string): Promise<ShoppingListItem> {
    return this.updateItem(id, { purchased: true });
  },

  async markAsUnpurchased(id: string): Promise<ShoppingListItem> {
    return this.updateItem(id, { purchased: false });
  },
};
